// Tiny, safe Markdown renderer for admin-authored blog posts.
// Supports: paragraphs, headings (##, ###), bold, italic, links, unordered/ordered lists, blockquotes, and line breaks.
// Escapes all raw HTML to prevent XSS.

const esc = (s) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const inline = (s) => {
  let out = esc(s);
  // links [text](url) — url is escaped above, so we sanitize protocol
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (m, text, href) => {
    const safeHref = /^(https?:|mailto:|tel:|\/)/i.test(href) ? href : '#';
    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });
  // bold **text**
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // italic *text* or _text_
  out = out.replace(/(^|[\s(])\*([^*]+)\*(?=$|[\s.,;:!?)])/g, '$1<em>$2</em>');
  out = out.replace(/(^|[\s(])_([^_]+)_(?=$|[\s.,;:!?)])/g, '$1<em>$2</em>');
  return out;
};

export function renderMarkdown(text) {
  if (!text) return '';
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const html = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Blank line: paragraph break
    if (!line.trim()) { i++; continue; }

    // Heading
    const h = line.match(/^(#{2,3})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      html.push(`<h${level} class="font-serif-display text-[color:var(--deep-sage)] mt-6 mb-2 ${level === 2 ? 'text-2xl' : 'text-xl'}">${inline(h[2])}</h${level}>`);
      i++;
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      html.push(`<blockquote class="border-l-4 pl-4 italic text-[color:var(--taupe-gray)] my-4" style="border-color: var(--dusty-rose);">${inline(buf.join(' '))}</blockquote>`);
      continue;
    }

    // Unordered list
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^[-*]\s+/, ''))}</li>`);
        i++;
      }
      html.push(`<ul class="list-disc pl-6 space-y-1 my-3">${items.join('')}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\d+\.\s+/, ''))}</li>`);
        i++;
      }
      html.push(`<ol class="list-decimal pl-6 space-y-1 my-3">${items.join('')}</ol>`);
      continue;
    }

    // Paragraph: collect until blank line
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{2,3}\s|>|[-*]\s|\d+\.\s)/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    html.push(`<p>${inline(buf.join(' '))}</p>`);
  }
  return html.join('\n');
}
